import * as m from "@/paraglide/messages"
import type { ArchetypeId, PersonalityBigFive, PersonalityHumor } from "./types"

type Message = () => string

const ARCHETYPE_LABELS: Record<ArchetypeId, Message> = {
	seeker: m.personalityArchetypeLabelSeeker,
	creator: m.personalityArchetypeLabelCreator,
	realist: m.personalityArchetypeLabelRealist,
	jester: m.personalityArchetypeLabelJester,
	aesthete: m.personalityArchetypeLabelAesthete,
	support: m.personalityArchetypeLabelSupport,
	thinker: m.personalityArchetypeLabelThinker,
	rebel: m.personalityArchetypeLabelRebel,
	achiever: m.personalityArchetypeLabelAchiever,
	leader: m.personalityArchetypeLabelLeader,
	dreamer: m.personalityArchetypeLabelDreamer,
	visionary: m.personalityArchetypeLabelVisionary,
}

const ARCHETYPE_DESCRIPTIONS: Record<ArchetypeId, Message> = {
	seeker: m.personalityArchetypeDescriptionSeeker,
	creator: m.personalityArchetypeDescriptionCreator,
	realist: m.personalityArchetypeDescriptionRealist,
	jester: m.personalityArchetypeDescriptionJester,
	aesthete: m.personalityArchetypeDescriptionAesthete,
	support: m.personalityArchetypeDescriptionSupport,
	thinker: m.personalityArchetypeDescriptionThinker,
	rebel: m.personalityArchetypeDescriptionRebel,
	achiever: m.personalityArchetypeDescriptionAchiever,
	leader: m.personalityArchetypeDescriptionLeader,
	dreamer: m.personalityArchetypeDescriptionDreamer,
	visionary: m.personalityArchetypeDescriptionVisionary,
}

type BigFiveScaleId = Exclude<keyof PersonalityBigFive, "description">

// Display order of the scales and their poles: 0 is the left pole, 100 the right one.
const BIG_FIVE_SCALES: ReadonlyArray<
	readonly [BigFiveScaleId, Message, Message]
> = [
	[
		"openness",
		m.personalityBigFiveOpennessLeft,
		m.personalityBigFiveOpennessRight,
	],
	[
		"conscientiousness",
		m.personalityBigFiveConscientiousnessLeft,
		m.personalityBigFiveConscientiousnessRight,
	],
	[
		"extraversion",
		m.personalityBigFiveExtraversionLeft,
		m.personalityBigFiveExtraversionRight,
	],
	[
		"agreeableness",
		m.personalityBigFiveAgreeablenessLeft,
		m.personalityBigFiveAgreeablenessRight,
	],
	[
		"emotional_stability",
		m.personalityBigFiveEmotionalStabilityLeft,
		m.personalityBigFiveEmotionalStabilityRight,
	],
]

export interface BigFiveScale {
	id: BigFiveScaleId
	left: string
	right: string
	value: number
}

// Counterclockwise from east, 45° per sector.
const HUMOR_STYLES: readonly Message[] = [
	m.personalityHumorStyleEast,
	m.personalityHumorStyleNorthEast,
	m.personalityHumorStyleNorth,
	m.personalityHumorStyleNorthWest,
	m.personalityHumorStyleWest,
	m.personalityHumorStyleSouthWest,
	m.personalityHumorStyleSouth,
	m.personalityHumorStyleSouthEast,
]

export function getArchetypeLabel(id: ArchetypeId): string {
	return ARCHETYPE_LABELS[id]()
}

export function getArchetypeDescription(id: ArchetypeId): string {
	return ARCHETYPE_DESCRIPTIONS[id]()
}

export function getBigFiveScales(bigFive: PersonalityBigFive): BigFiveScale[] {
	return BIG_FIVE_SCALES.map(([id, left, right]) => ({
		id,
		left: left(),
		right: right(),
		value: bigFive[id],
	}))
}

// X: harsh (-100) → kind (100), Y: about self (-100) → about others (100).
// Same sectors as the backend: the center counts as east.
export function getHumorStyleTitle(humor: PersonalityHumor): string {
	const angle = (Math.atan2(humor.y_axis, humor.x_axis) * 180) / Math.PI
	const sector = Math.round((angle + 360) / 45) % 8
	const style = HUMOR_STYLES[sector] ?? m.personalityHumorStyleEast
	return style()
}
